import { useTranslation } from '@utils/i18n/i18n.consumers';
import { Icon } from '@nazka/nazka.mapframe.components'
import { faTemperatureHigh, faEye } from '@fortawesome/pro-light-svg-icons'
import { faTemperatureSun } from '@fortawesome/pro-solid-svg-icons';
import theme from '@css/theme.style.json';

import RiskDataInfoTable from './RiskDataInfoTable/RiskDataInfoTable';

import './risk-data-info-component.styl'

function RiskDataInfoComponent() {
  const { t } = useTranslation();
  return (
    <div className="risk-data-info">
      <h2>{t('How do we know about climate risks in the future')}</h2>

      <p>
        {t('To estimate how climate change might impact communities, economic activities and ecosystems in different regions we need to understand the current climate in the region and its natural variations, and how it is projected to change.')}
      </p>

      <p>
        {t('Current climate and climate variability are characterised using different sources of information including weather stations, satellite data, reanalysis (reconstruction of the past that combine modelled and observed data), etc. This information can then be used to evaluate the present level of risks (e.g. is the region already affected by extreme heat), identify current exposure and vulnerability ( e.g. is there a large proportion of very young and elderly people more vulnerable to extreme temperatures), and capacity to respond (e.g. accessibility to fans, or cooling centres).')}
      </p>

      <p>
        {t('Projections of climate change are obtained using computer models that can simulate the different components of the climate system: atmosphere, ocean, ice masses, biosphere, etc, and their interactions. These models can simulate for instance how temperature could increase in the future, whether heat waves will become more frequent or intense, which regions will become more prone to flooding or droughts, etc.')}
      </p>

      <p>
        {t('However to do that the computational models need to know about how greenhouse gases and other anthropogenic influences (e.g. deforestation, changes in the land use, etc) will change in the future. These changes are encapsulated in the so called “Shared Socioeconomic Pathways” or SSPs, which are scenarios about how different countries and communities will evolve, what type of economic activities will be dominant, how countries will cooperate (or not), how population will increase, what sources of energy will be employed (fossil fuels vs renewable energies), etc.')}
      </p>

      <p>{t('In this tool, two SSPs are considered:')}</p>
      <ul>
        <li>
          <strong>{t('Medium emissions scenario (SSP2-4.5)')}</strong>
          : 
          {' '}
          {t('that represents a World where emissions of Green House Gases are kept at medium levels, and global warming by the end of this century reaches about 2.7°C.')}
        </li>
        <li>
          <strong>{t('High emissions scenario (SSP5-8.5)')}</strong>
          : 
          {' '}
          {t('that represents a World with very high emissions of Green House Gases, and global warming by the end of this century reaches about 4.4°C.')}
        </li>
      </ul>

      <h2>{t('Climate risk data explained')}</h2>

      <p>
        {t('As discussed in <a href="/learn/module/1/session/2/part/1">module 1</a> while climate hazard refers to the extreme weather events (such as floods, or wildfires) or long-term shifts (such as sea level rise) that can cause harm, climate risk considers the existence of exposed population or assets in the hazard’s way, and their vulnerability and capacity to address or adapt to its impacts.')}
      </p>

      <p>
        {t('Here we use the term climate risk to indicate the intensity and/or frequency of the hazards that can cause damage, and how this might change in the future. Except for riverine flooding, no information is included about exposed population/communities, and their vulnerabilities and adaptive capacity. ')}
      </p>

      <p>{t('Five hazards grouped in three types are considered:')}</p>

      <RiskDataInfoTable />

      <p>{t('The colours represent the amount of risk:')}</p>
      <ul>
        <li className="risk-data-info__legend-item">
          <div className="risk-data-info__color-box" style={{ backgroundColor: 'grey' }} /> 
          {' '}
          {t('unknown')}
        </li>
        <li className="risk-data-info__legend-item">
          <div className="risk-data-info__color-box" style={{ backgroundColor: theme.color.primary.main }} /> 
          {' '}
          {t('low')}
        </li>
        <li className="risk-data-info__legend-item">
          <div className="risk-data-info__color-box" style={{ backgroundColor: 'rgb(254, 204, 55)' }} /> 
          {' '}
          {t('medium')}
        </li>
        <li className="risk-data-info__legend-item">
          <div className="risk-data-info__color-box" style={{ backgroundColor: 'rgb(254, 135, 55)' }} /> 
          {' '}
          {t('high')}
        </li>
        <li className="risk-data-info__legend-item">
          <div className="risk-data-info__color-box" style={{ backgroundColor: 'rgb(254, 55, 91)' }} /> 
          {' '}
          {t('very high')}
        </li>
      </ul>

      <p>
        <span className="risk-data-info__inline">
          {t('The overview')}
          <Icon icon={faEye} size="20px" color={theme.color.primary.main} className="risk-data-info__icon" />
        </span>
        {' '}
        {t('shows a summary of the risks for all hazards for the selected district in Malawi on the left. You can select the district of your choice on the map on the right.')}
      </p>

      <p>
        <span className="risk-data-info__inline">
          {t('For the other types, for instance temperature')}
          <Icon icon={faTemperatureHigh} size="20px" color={theme.color.primary.main} className="risk-data-info__icon" />
        </span>
        {' '}
        {t('more detailed information is available per hazard category.')}
        {' '}
        <span className="risk-data-info__inline">
          {t('for instance heat wave')}
          <Icon icon={faTemperatureSun} size="20px" color={theme.color.primary.main} className="risk-data-info__icon" />
        </span>
        {' '}
        {t('for Kasungu on the left. In this case, on the map on the right, the risk of heat waves for all Malawi’s districts for one representative point is visible.')}
      </p>

      <p>
        {t('The displayed risk depends on the selected emission scenario: medium or high. And on the selected time period: baseline (1995-2024), or projection periods: 2025-2054 or 2055-2084.')}
      </p>
    </div>
  )
}

export default RiskDataInfoComponent